<script setup lang="ts">
import {
  effect,
  getValue,
  peekValue,
  type ComponentNode,
  type NodeProps,
} from '@a2ui/web_core/v0_9'
import { A2UI_ICONS, unwrap, writeBack } from '~/utils/a2ui'

const { node } = defineProps<{ node: ComponentNode }>()

/* Bridge the node's signal into Vue; resubscribe when the resolver swaps the node
 * (e.g. a placeholder upgraded in place). */
const p = shallowRef<NodeProps>(peekValue(node.props))
watchEffect((onCleanup) => {
  const subject = node
  onCleanup(
    effect(() => {
      p.value = getValue(subject.props)
    }),
  )
})

const child = (key: string) => p.value[key] as ComponentNode | undefined
const children = computed(() => (p.value.children ?? []) as ComponentNode[])
const text = (key: string) => unwrap<string>(p.value[key]) ?? ''
/* Agent-supplied URLs are untrusted; a remote src would leak the user's IP to
 * an attacker-chosen host, so only inline data of the matching type renders. */
const mediaUrl = (kind: 'image' | 'video' | 'audio') => {
  const url = text('url')
  return url.toLowerCase().startsWith(`data:${kind}/`) ? url : undefined
}

const JUSTIFY: Record<string, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  spaceBetween: 'justify-between',
  spaceAround: 'justify-around',
  spaceEvenly: 'justify-evenly',
  stretch: 'justify-stretch',
}
const ALIGN: Record<string, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
}
const flexClass = computed(() => [
  JUSTIFY[p.value.justify as string],
  ALIGN[p.value.align as string],
])

const TEXT: Record<string, { tag: string; class: string }> = {
  h1: { tag: 'h2', class: 'text-xl font-semibold text-highlighted' },
  h2: { tag: 'h3', class: 'text-lg font-semibold text-highlighted' },
  h3: { tag: 'h4', class: 'text-base font-semibold text-highlighted' },
  h4: { tag: 'h5', class: 'text-sm font-semibold text-highlighted' },
  h5: { tag: 'h6', class: 'text-sm font-medium text-highlighted' },
  caption: { tag: 'p', class: 'text-xs text-muted' },
  body: { tag: 'p', class: 'text-sm' },
}

const IMAGE: Record<string, string> = {
  icon: 'size-6',
  avatar: 'size-10 rounded-full',
  smallFeature: 'max-w-32 rounded-md',
  mediumFeature: 'max-w-64 rounded-md',
  largeFeature: 'w-full rounded-md',
  header: 'w-full max-h-48 rounded-md',
}
const FIT: Record<string, string> = {
  contain: 'object-contain',
  cover: 'object-cover',
  fill: 'object-fill',
  none: 'object-none',
  scaleDown: 'object-scale-down',
}

const icon = computed(() => unwrap<string | { svgPath: string }>(p.value.name))

const tabs = computed(() =>
  ((p.value.tabs ?? []) as { title: unknown; child: ComponentNode }[]).map(
    (tab, index) => ({
      label: unwrap<string>(tab.title),
      value: String(index),
      node: tab.child,
    }),
  ),
)

const options = computed(() =>
  ((p.value.options ?? []) as { label: unknown; value: string }[]).map(
    (option) => ({ label: unwrap<string>(option.label), value: option.value }),
  ),
)
const selected = computed(() => unwrap<string[]>(p.value.value) ?? [])

/* Validation messages only after the user has edited the field. */
const touched = ref(false)
const fieldError = computed(() =>
  touched.value
    ? (p.value.validationErrors as string[] | undefined)?.[0]
    : undefined,
)
const onInput = (value: unknown) => {
  touched.value = true
  writeBack(p.value.value, value)
}

/* ponytail: datetime-local has no zone, so an ISO value is shown in UTC wall
 * time; parse with the user's zone if agents start sending offsets. */
const dateTimeType = computed(() =>
  p.value.enableTime === false
    ? 'date'
    : p.value.enableDate === false
      ? 'time'
      : 'datetime-local',
)
const dateTimeValue = computed(() => {
  const value = unwrap<string>(p.value.value) ?? ''
  if (dateTimeType.value === 'date') return value.slice(0, 10)
  if (dateTimeType.value === 'time')
    return value.includes('T') ? value.slice(11, 16) : value
  return value.slice(0, 16)
})
</script>

<template>
  <div
    class="min-w-0"
    :style="p.weight ? { flex: p.weight as number } : undefined"
  >
    <USkeleton v-if="node.state === 'pending'" class="h-4 w-24" />

    <p v-else-if="node.state !== 'resolved'" class="text-muted text-xs">
      Unsupported component: {{ node.type }}
    </p>

    <component
      :is="TEXT[p.variant as string]?.tag ?? 'p'"
      v-else-if="node.type === 'Text'"
      class="whitespace-pre-wrap"
      :class="TEXT[p.variant as string]?.class ?? 'text-sm'"
    >
      {{ text('text') }}
    </component>

    <img
      v-else-if="node.type === 'Image'"
      :class="[
        IMAGE[p.variant as string] ?? 'max-w-full rounded-md',
        FIT[p.fit as string],
      ]"
      :alt="text('description')"
      :src="mediaUrl('image')"
    />

    <svg
      v-else-if="node.type === 'Icon' && typeof icon === 'object'"
      class="size-5"
      aria-hidden="true"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path :d="icon.svgPath" />
    </svg>
    <UIcon
      v-else-if="node.type === 'Icon'"
      :name="A2UI_ICONS[icon as string] ?? 'i-lucide-circle'"
      class="size-5"
    />

    <video
      v-else-if="node.type === 'Video'"
      class="w-full rounded-md"
      :src="mediaUrl('video')"
      controls
    />

    <audio
      v-else-if="node.type === 'AudioPlayer'"
      class="w-full"
      :aria-label="text('description')"
      :src="mediaUrl('audio')"
      controls
    />

    <div
      v-else-if="node.type === 'Row' || node.type === 'Column'"
      class="flex gap-2"
      :class="[
        node.type === 'Row' ? 'flex-row flex-wrap' : 'flex-col',
        flexClass,
      ]"
    >
      <A2uiNode v-for="item in children" :key="item.instanceId" :node="item" />
    </div>

    <div
      v-else-if="node.type === 'List'"
      class="flex gap-2"
      :class="[
        p.direction === 'horizontal' ? 'flex-row overflow-x-auto' : 'flex-col',
        ALIGN[p.align as string],
      ]"
    >
      <A2uiNode v-for="item in children" :key="item.instanceId" :node="item" />
    </div>

    <UCard
      v-else-if="node.type === 'Card'"
      :ui="{ body: 'p-3 sm:p-3' }"
      variant="outline"
    >
      <A2uiNode v-if="child('child')" :node="child('child')!" />
    </UCard>

    <UTabs
      v-else-if="node.type === 'Tabs'"
      :items="tabs"
      default-value="0"
      size="xs"
      variant="link"
    >
      <template #content="{ item }">
        <A2uiNode class="pt-2" :node="item.node" />
      </template>
    </UTabs>

    <UModal v-else-if="node.type === 'Modal'">
      <A2uiNode v-if="child('trigger')" :node="child('trigger')!" />
      <template #body>
        <A2uiNode v-if="child('content')" :node="child('content')!" />
      </template>
    </UModal>

    <USeparator
      v-else-if="node.type === 'Divider'"
      :orientation="p.axis === 'vertical' ? 'vertical' : 'horizontal'"
    />

    <UButton
      v-else-if="node.type === 'Button'"
      :color="p.variant === 'primary' ? 'primary' : 'neutral'"
      :disabled="p.isValid === false"
      :variant="
        p.variant === 'primary'
          ? 'solid'
          : p.variant === 'borderless'
            ? 'ghost'
            : 'outline'
      "
      size="sm"
      @click="(p.action as (() => void) | undefined)?.()"
    >
      <A2uiNode v-if="child('child')" :node="child('child')!" />
    </UButton>

    <UFormField
      v-else-if="node.type === 'TextField'"
      :error="fieldError"
      :label="text('label')"
    >
      <UTextarea
        v-if="p.variant === 'longText'"
        class="w-full"
        :model-value="text('value')"
        autoresize
        @update:model-value="onInput"
      />
      <UInput
        v-else
        class="w-full"
        :model-value="text('value')"
        :type="
          p.variant === 'number'
            ? 'number'
            : p.variant === 'obscured'
              ? 'password'
              : 'text'
        "
        @update:model-value="onInput"
      />
    </UFormField>

    <UCheckbox
      v-else-if="node.type === 'CheckBox'"
      :label="text('label')"
      :model-value="unwrap<boolean>(p.value) ?? false"
      @update:model-value="onInput"
    />

    <UFormField
      v-else-if="node.type === 'ChoicePicker'"
      :error="fieldError"
      :label="text('label')"
    >
      <UCheckboxGroup
        v-if="p.variant === 'multipleSelection'"
        :items="options"
        :model-value="selected"
        :orientation="p.displayStyle === 'chips' ? 'horizontal' : 'vertical'"
        :variant="p.displayStyle === 'chips' ? 'card' : 'list'"
        @update:model-value="onInput"
      />
      <URadioGroup
        v-else
        :items="options"
        :model-value="selected[0]"
        :orientation="p.displayStyle === 'chips' ? 'horizontal' : 'vertical'"
        :variant="p.displayStyle === 'chips' ? 'card' : 'list'"
        @update:model-value="onInput([$event])"
      />
    </UFormField>

    <UFormField
      v-else-if="node.type === 'Slider'"
      :hint="String(unwrap<number>(p.value) ?? '')"
      :label="text('label')"
    >
      <USlider
        :max="p.max as number"
        :min="(p.min as number | undefined) ?? 0"
        :model-value="unwrap<number>(p.value)"
        @update:model-value="onInput"
      />
    </UFormField>

    <UFormField
      v-else-if="node.type === 'DateTimeInput'"
      :error="fieldError"
      :label="text('label')"
    >
      <UInput
        class="w-full"
        :model-value="dateTimeValue"
        :type="dateTimeType"
        @update:model-value="onInput"
      />
    </UFormField>
  </div>
</template>
